//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import BlocksRenderer from './toolResults/BlocksRenderer';
import type {
  ToolOutputRef,
  ToolResultBlock,
  ToolStatus,
} from '../../../types/streaming';
import {
  downloadKey,
  getDownloadState,
  setDownloadState,
  useDownloadState,
} from './toolResults/downloadState';
import { useToolOutputDownload } from './toolResults/ToolOutputDownloadContext';

export interface ToolCallDetailsProps {
  toolArgs: Record<string, unknown>;
  toolResult?: string;
  resultBlocks?: ToolResultBlock[];
  toolStatus: ToolStatus;
  // Spec 007: set when the output was too large to keep as text. Replaces the
  // OUTPUT body with a size message and a Download button.
  outputRef?: ToolOutputRef;
  toolCallId?: string;
}

// Values longer than this are clamped to a few lines with a Show more/less
// toggle — long values (e.g. a free-text description arg) shouldn't be able
// to blow out the card's height.
const ARG_VALUE_CLAMP_CHARS = 200;

function formatArgValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
}

const ArgValue: React.FC<{ value: unknown }> = ({ value }) => {
  const [expanded, setExpanded] = useState(false);
  const text = formatArgValue(value);
  const isLong = text.length > ARG_VALUE_CLAMP_CHARS;
  return (
    <div className="min-w-0">
      <span
        className={cn(
          'block whitespace-pre-wrap break-all',
          isLong && !expanded && 'line-clamp-3',
        )}
      >
        {text}
      </span>
      {isLong && (
        <button
          type="button"
          className="mt-0.5 text-[10px] font-medium text-violet-700 hover:underline"
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

// Shown instead of the output text when the output was too large to keep as text (spec 007).
// The text never reaches the page; the button downloads the complete original.
const LargeOutputNotice: React.FC<{
  outputRef: ToolOutputRef;
  toolCallId?: string;
  isError: boolean;
}> = ({ outputRef, toolCallId, isError }) => {
  const { download } = useToolOutputDownload();
  const key = downloadKey(outputRef.conversationId, toolCallId ?? '');
  const { busy, problem } = useDownloadState(key);
  const [showModelView, setShowModelView] = useState(false);

  const onDownload = async () => {
    // Read the store, not the render's `busy`: a second click, or a re-opened row, must
    // not start a second download while one is running.
    if (!download || !toolCallId || getDownloadState(key).busy) return;
    setDownloadState(key, { busy: true, problem: '' });
    try {
      await download(toolCallId, outputRef.conversationId);
      setDownloadState(key, { busy: false, problem: '' });
    } catch (error) {
      setDownloadState(key, {
        busy: false,
        problem:
          error instanceof Error && error.message
            ? error.message
            : 'The download failed. Try again.',
      });
    }
  };

  return (
    <div
      data-testid="tool-output-too-large"
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border bg-white p-2 text-[11px]',
        isError
          ? 'border-red-300 text-red-600'
          : 'border-gray-200 text-gray-700',
      )}
    >
      <span>
        Output too large ({outputRef.chars.toLocaleString('en-US')} characters)
      </span>
      <button
        type="button"
        data-testid="tool-output-download"
        className="rounded border border-violet-300 px-2 py-0.5 font-medium text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={!download || !toolCallId || busy}
        onClick={onDownload}
      >
        {busy ? 'Downloading…' : 'Download'}
      </button>
      {problem && (
        <span role="alert" className="w-full text-red-600">
          {problem}
        </span>
      )}
      {outputRef.modelView && (
        <div className="w-full">
          <button
            type="button"
            data-testid="tool-output-model-view-toggle"
            className="text-[10px] font-medium text-violet-700 hover:underline"
            aria-expanded={showModelView}
            onClick={() => setShowModelView((open) => !open)}
          >
            {showModelView ? 'Hide what the model saw' : 'What the model saw'}
          </button>
          {showModelView && (
            <>
              <div className="mt-1 text-[10px] italic text-slate-400">
                The assistant received this short summary instead of the full
                output, and reads more of it on demand.
              </div>
              <pre
                data-testid="tool-output-model-view"
                className="m-0 mt-1 max-h-[240px] overflow-auto whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-700"
              >
                {outputRef.modelView}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// The INPUT/OUTPUT body shared by ToolCallBox (the inline card) and
// TurnTracePanel (the record's expanded row) — extracted so both reuse one
// rendering path for tables, code, JSON, links, and images (AC8) instead of
// each re-implementing result rendering. Behavior-identical to what
// ToolCallBox rendered before the extraction; verify against
// ToolCallBox.test.tsx if this ever needs to change.
//
// OUTPUT is omitted entirely while pending_approval — there is nothing to
// show yet, and ToolCallBox renders its approval actions in that slot
// instead. A gated call still awaiting a decision, if expanded from the
// trace record, correctly shows no output for the same reason.
const ToolCallDetails: React.FC<ToolCallDetailsProps> = ({
  toolArgs,
  toolResult,
  resultBlocks,
  toolStatus,
  outputRef,
  toolCallId,
}) => (
  <>
    <div className="mb-1 mt-2 text-[10px] font-semibold uppercase tracking-[0.5px] text-slate-400">
      INPUT
    </div>
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white text-[11px] text-gray-700">
      {Object.keys(toolArgs).length === 0 ? (
        <div className="p-2 italic text-slate-400">No arguments</div>
      ) : (
        Object.entries(toolArgs).map(([key, value], i) => (
          <div
            key={key}
            className={cn(
              'grid grid-cols-[minmax(80px,auto)_1fr] gap-x-3 p-2',
              i > 0 && 'border-t border-gray-100',
            )}
          >
            <span className="truncate font-medium text-slate-500" title={key}>
              {key}
            </span>
            <ArgValue value={value} />
          </div>
        ))
      )}
    </div>

    {toolStatus !== 'pending_approval' && (
      <>
        <div className="mb-1 mt-2 text-[10px] font-semibold uppercase tracking-[0.5px] text-slate-400">
          OUTPUT
        </div>
        {outputRef ? (
          <LargeOutputNotice
            outputRef={outputRef}
            toolCallId={toolCallId}
            isError={toolStatus === 'error'}
          />
        ) : toolStatus === 'running' &&
          !toolResult &&
          !(resultBlocks && resultBlocks.length) ? (
          <div className="py-1 text-[11px] italic text-slate-400">
            Waiting for result…
          </div>
        ) : resultBlocks && resultBlocks.length > 0 ? (
          <BlocksRenderer
            blocks={resultBlocks}
            isError={toolStatus === 'error'}
          />
        ) : (
          <pre
            className={
              toolStatus === 'error'
                ? 'm-0 max-h-[320px] min-h-[60px] overflow-auto whitespace-pre-wrap break-all rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-600'
                : 'm-0 max-h-[320px] min-h-[60px] overflow-auto whitespace-pre-wrap break-all rounded-md border border-gray-200 bg-white p-2 text-[11px] text-gray-700'
            }
          >
            {toolResult || ''}
          </pre>
        )}
      </>
    )}
  </>
);

export default ToolCallDetails;
